import { inject, Injectable } from '@angular/core';
import { ConsultationStatus } from '@enums/consultation-status';
import { ConsultationStorageKeys } from '@enums/consultation-storage-keys';
import { CustomerTestsTypes } from '@enums/customer-tests-types';
import { QuestionnaireStorageKeys } from '@enums/questionnaire-storage-keys';
import { Treatment } from '@enums/treatment';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { Address } from '@models/address';
import { Center } from '@models/center';
import { ConsultationRequestAdditionalInfo } from '@models/consultation-request/consultation-request-additional-info';
import { ConsultationRequestPharmacy } from '@models/consultation-request/consultation-request-pharmacy';
import { CustomerTest } from '@models/customer-test';
import { Questionnaire } from '@models/dynamic-forms/questionnaire';
import { QuestionnaireAnswers } from '@models/dynamic-forms/questionnaire-answers';
import { OrderTest } from '@models/order-test';
import { Recharge } from '@models/recharge';
import { Test } from '@models/test';
import { UpsellAddress } from '@models/upsell-address';
import { AbstractStorageService } from '@services/abstract-storage.service';
import { ErrorLoggingService } from '@services/error-logging.service';

@Injectable({
  providedIn: 'root',
})
/**
 * This service class provides the consultation storage management for the application.
 */
export class ConsultationStorageService extends AbstractStorageService {
  private readonly errorLoggingService = inject(ErrorLoggingService);

  /**
   * Get treatment preference tests from localStorage.
   */
  public get treatmentPreferenceTests(): Test[] {
    return this.get(ConsultationStorageKeys.TreatmentPreferenceTests) ?? [];
  }

  /**
   * Get the treatment preferences from localStorage.
   */
  public get treatmentPreferences(): string | null {
    return this.get(ConsultationStorageKeys.TreatmentPreferences);
  }

  /**
   * Get the choose a lab value from localStorage.
   */
  public get hasMandatoryLabResult(): string | null {
    return this.get(ConsultationStorageKeys.HasMandatoryLabResult);
  }

  /**
   * Get the consultation id.
   */
  public get consultationId(): string | null {
    return localStorage.getItem(this.getFullKey(ConsultationStorageKeys.ConsultationId, true));
  }

  /**
   * Get the hash from localStorage.
   */
  public get hash(): string | null {
    return this.get(ConsultationStorageKeys.Hash);
  }

  /**
   * Get the questionnaire from localStorage.
   */
  public get questionnaire(): any {
    return this.get(ConsultationStorageKeys.Questionnaire);
  }

  /**
   * Get the pharmacy from localStorage.
   */
  public get pharmacy(): ConsultationRequestPharmacy | null {
    return this.get(ConsultationStorageKeys.Pharmacy);
  }

  /**
   * Get the address from localStorage.
   */
  public get address(): Address | null {
    return this.get(ConsultationStorageKeys.Address);
  }

  /**
   * Get the lab from localStorage.
   */
  public get lab(): Center | null {
    return this.get(ConsultationStorageKeys.Lab);
  }

  /**
   * Get the shippingAddress from localStorage.
   */
  public get shippingAddress(): Address | null {
    return this.get(ConsultationStorageKeys.ShippingAddress);
  }

  /**
   * Get the shipping address ID from localStorage.
   */
  public get shippingAddressId(): number | null {
    return this.get(ConsultationStorageKeys.ShippingAddressId);
  }

  /**
   * Retrieve upsell transaction id
   */
  public get upsells(): string[] {
    return this.get(ConsultationStorageKeys.Upsells) ?? [];
  }

  /**
   * Retrieve current consultation request status.
   */
  public get consultationStatus(): ConsultationStatus | null {
    return this.get(ConsultationStorageKeys.ConsultationStatus);
  }

  /**
   * Check if the consultation status is disqualified
   */
  public get isConsultationDisqualified(): boolean {
    return this.get(ConsultationStorageKeys.ConsultationStatus) === ConsultationStatus.Disqualified;
  }

  /**
   * Retrieve the recharge from localStorage.
   */
  public get recharge(): Recharge | null {
    return this.get(ConsultationStorageKeys.Recharge);
  }

  /**
   * Retrieve the BMI score from localStorage.
   */
  public get bmiScore(): number | null {
    return this.get(ConsultationStorageKeys.BmiScore);
  }

  /**
   * Retrieve the upsell address from localStorage.
   */
  public get upsellAddress(): UpsellAddress | null {
    return this.get(ConsultationStorageKeys.UpsellAddress);
  }

  /**
   * Retrieve the remaining balance from localStorage.
   */
  public get remainingBalance(): number | null {
    return this.get(ConsultationStorageKeys.RemainingBalance);
  }

  /**
   * Gets the medical history questionnaire answers from storage.
   *
   * @returns {QuestionnaireAnswers} the stored medical history questionnaire answers
   */
  public get medicalHistoryQuestionnaireAnswers(): QuestionnaireAnswers | null {
    return this.getQuestionnaireAnswers('medicalHistory');
  }

  /**
   * Gets the upsell upgrades from storage.
   *
   * @returns {QuestionnaireAnswers} the stored upsell upgrades or empty object if not found
   */
  public get upsellUpgrades(): QuestionnaireAnswers {
    return this.get(ConsultationStorageKeys.UpsellUpgrades) ?? {};
  }

  /**
   * Gets the partner email from storage.
   *
   * @returns {string} the stored partner email
   */
  public get partnerEmail(): string | null {
    return this.get(ConsultationStorageKeys.PartnerEmail);
  }

  /**
   * Gets the ordered tests from storage.
   *
   * @returns {OrderTest[]} the stored ordered tests or empty array if not found
   */
  public get orderedTests(): OrderTest[] {
    return this.get(ConsultationStorageKeys.OrderedTests) ?? [];
  }

  /**
   * Gets the customer tests from storage.
   *
   * @returns {CustomerTest[]} the customer tests or empty array if not found
   */
  public get customerTests(): CustomerTest[] {
    return this.get(ConsultationStorageKeys.CustomerTests) ?? [];
  }

  /**
   * Retrieves the anonymous token for creating attachments from local storage.
   *
   * This token is used to authenticate attachment uploads API v2 endpoint.
   * It is part of our effort to replace our API v1 usage with API v2.
   */
  public get createAttachmentToken(): string | null {
    return this.get(ConsultationStorageKeys.CreateAttachmentToken);
  }

  /**
   * Retrieves whether an ID attachment upload to the API has been completed.
   */
  public get idAttachmentUploaded(): boolean {
    return this.get(ConsultationStorageKeys.IdAttachmentUploaded, false) ?? false;
  }

  /**
   * Retrieves the OnceHub ID from local storage.
   */
  public get scheduleOnceId(): string | null {
    return this.get(ConsultationStorageKeys.ScheduleOnceId, false);
  }

  /**
   * Retrieves the additional info for the consultation request from local storage.
   */
  public get consultationAdditionalInfo(): ConsultationRequestAdditionalInfo | null {
    return this.get(ConsultationStorageKeys.ConsultationAdditionalInfo, false);
  }

  /**
   * Retrieves the treatment_for from local storage.
   */
  public get treatmentFor(): Treatment | null {
    return this.get(ConsultationStorageKeys.TreatmentFor, false);
  }

  /**
   * Gets the storage suffix based on the consultation ID.
   *
   * @returns {string} the storage suffix
   */
  private get storageSuffix(): string {
    return `-consultation${this.consultationId ? `-${this.consultationId}` : ''}`;
  }

  /**
   * Set treatment preference tests on localStorage.
   *
   * @param {Test[]} tests The treatment preference tests to be set.
   */
  public set treatmentPreferenceTests(tests: Test[]) {
    this.set(ConsultationStorageKeys.TreatmentPreferenceTests, tests);
  }

  /**
   * Set the patient on localStorage.
   *
   * @param {string} treatmentPreferences The treatment preferences to be set.
   */
  public set treatmentPreferences(treatmentPreferences: string) {
    this.set(ConsultationStorageKeys.TreatmentPreferences, treatmentPreferences);
  }

  /**
   * Set the choose a lab value on localStorage.
   *
   * @param {boolean} hasMandatoryLabResult Has Mandatory Lab Result Value.
   */
  public set hasMandatoryLabResult(hasMandatoryLabResult: string) {
    this.set(ConsultationStorageKeys.HasMandatoryLabResult, hasMandatoryLabResult);
  }

  /**
   * Set the hash on localStorage.
   *
   * @param {string} hash The hash to be set.
   */
  public set hash(hash: string) {
    this.set(ConsultationStorageKeys.Hash, hash);
  }

  /**
   * Set the consultation id on localStorage.
   *
   * @param {string} consultationId The consultation id to be set.
   */
  public set consultationId(consultationId: string) {
    localStorage.setItem(this.getFullKey(ConsultationStorageKeys.ConsultationId, true), consultationId);
  }

  /**
   * Set the questionnaire on localStorage.
   *
   * @param {any} questionnaire The questionnaire to be set.
   */
  public set questionnaire(questionnaire: any) {
    this.set(ConsultationStorageKeys.Questionnaire, questionnaire);
  }

  /**
   * Set the pharmacy on localStorage.
   *
   * @param {any} pharmacy The pharmacy to be set.
   */
  public set pharmacy(pharmacy: any) {
    this.set(ConsultationStorageKeys.Pharmacy, pharmacy);
  }

  /**
   * Set the address on localStorage.
   *
   * @param {Address} address The address to be set.
   */
  public set address(address: Address) {
    this.set(ConsultationStorageKeys.Address, address);
  }

  /**
   * Set the lab on localStorage.
   *
   * @param {Center} lab The address to be set.
   */
  public set lab(lab: Center) {
    this.set(ConsultationStorageKeys.Lab, lab);
  }

  /**
   * Set the shippingAddress on localStorage.
   *
   * @param {Address} address The address to be set.
   */
  public set shippingAddress(address: Address) {
    this.set(ConsultationStorageKeys.ShippingAddress, address);
  }

  /**
   * Set the shipping address ID on localStorage.
   *
   * @param {number | null} id The shipping address ID to be set.
   */
  public set shippingAddressId(id: number | null) {
    this.set(ConsultationStorageKeys.ShippingAddressId, id);
  }

  /**
   * Saves the upsells on localStorage.
   *
   * @param {any} upsells The upsells to be set.
   */
  public set upsells(upsells: any) {
    this.set(ConsultationStorageKeys.Upsells, upsells);
  }

  /**
   * Saves the upsells on localStorage.
   *
   * @param {ConsultationStatus} consultationStatus The consultation status.
   */
  public set consultationStatus(consultationStatus: ConsultationStatus) {
    this.set(ConsultationStorageKeys.ConsultationStatus, consultationStatus);
  }

  /**
   * Saves the recharge on localStorage.
   */
  public set recharge(recharge: Recharge) {
    this.set(ConsultationStorageKeys.Recharge, recharge);
  }

  /**
   * Saves the BMI score on localStorage.
   */
  public set bmiScore(bmiScore: number) {
    this.set(ConsultationStorageKeys.BmiScore, bmiScore);
  }

  /**
   * Saves the upsell address on localStorage.
   */
  public set upsellAddress(upsellAddress: UpsellAddress) {
    this.set(ConsultationStorageKeys.UpsellAddress, upsellAddress);
  }

  /**
   * Saves the remaining balance on localStorage.
   */
  public set remainingBalance(remainingBalance: number) {
    this.set(ConsultationStorageKeys.RemainingBalance, remainingBalance);
  }

  /**
   * Sets the medical history questionnaire answers in storage.
   *
   * @param {QuestionnaireAnswers} answers the medical history questionnaire answers to store
   */
  public set medicalHistoryQuestionnaireAnswers(answers: QuestionnaireAnswers) {
    this.setQuestionnaireAnswers('medicalHistory', answers);
  }

  /**
   * Sets the upsell upgrades in storage.
   *
   * @param {QuestionnaireAnswers} answers the upsell upgrades to store
   */
  public set upsellUpgrades(answers: QuestionnaireAnswers) {
    this.set(ConsultationStorageKeys.UpsellUpgrades, answers);
  }

  /**
   * Sets the partner email in storage.
   *
   * @param {string} email the partner email to store
   */
  public set partnerEmail(email: string) {
    this.set(ConsultationStorageKeys.PartnerEmail, email);
  }

  /**
   * Sets the ordered tests in storage.
   *
   * @param {OrderTest[]} orderedTests the array of ordered tests to store
   */
  public set orderedTests(orderedTests: OrderTest[]) {
    this.set(ConsultationStorageKeys.OrderedTests, orderedTests);
  }

  /**
   * Sets the customer tests in storage.
   *
   * @param {CustomerTest[]} customerTests the array of customer tests to store
   */
  public set customerTests(customerTests: CustomerTest[]) {
    this.set(ConsultationStorageKeys.CustomerTests, customerTests);
  }

  /**
   * Sets the anonymous token for creating attachments in local storage.
   */
  public set createAttachmentToken(token: string) {
    this.set(ConsultationStorageKeys.CreateAttachmentToken, token);
  }

  /**
   * Sets whether an ID attachment upload to the API has been completed.
   */
  public set idAttachmentUploaded(idAttachmentUploaded: boolean) {
    this.set(ConsultationStorageKeys.IdAttachmentUploaded, idAttachmentUploaded, false);
  }

  /**
   * Sets the OnceHub ID in local storage.
   */
  public set scheduleOnceId(scheduleOnceId: string) {
    this.set(ConsultationStorageKeys.ScheduleOnceId, scheduleOnceId, false);
  }

  /**
   * Sets the additional info for the consultation request in local storage.
   */
  public set consultationAdditionalInfo(info: ConsultationRequestAdditionalInfo) {
    this.set(ConsultationStorageKeys.ConsultationAdditionalInfo, info, false);
  }

  /**
   * Sets the treatment_for value in local storage.
   */
  public set treatmentFor(value: Treatment) {
    this.set(ConsultationStorageKeys.TreatmentFor, value, false);
  }

  /**
   * Get an address for a specific upsell slug
   * @param slug The UpsellSlugs enum value to retrieve the address for
   * @returns The stored Address or null if not found
   */
  public getUpsellAddress(slug: UpsellSlugs): Address | null {
    const addressMap = this.upsellAddress || {};

    return addressMap[slug] || null;
  }

  /**
   * Set an address for a specific upsell slug
   * @param slug The UpsellSlugs enum value to set an address for
   * @param address The Address to associate with the slug
   */
  public setUpsellAddress(slug: UpsellSlugs, address: Address): void {
    const addressMap = this.upsellAddress || {};
    this.upsellAddress = { ...addressMap, [slug]: address };
  }

  /**
   * Retrieves the name of the treatment from the treatment preferences, excluding the word 'Treatment'.
   *
   * @returns {string} the name of the treatment
   */
  public getTreatmentPreferenceName(): string {
    return this.treatmentPreferences?.replace('Treatment', '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Retrieves the ordered upsell names from the stored ordered tests.
   *
   * @returns {string[]} the ordered upsell names
   */
  public getOrderedUpsellNames(): string[] {
    return this.orderedTests
      .filter(({ customer_tests_type }) => customer_tests_type === CustomerTestsTypes.UpsellIndividual)
      .map(({ customer_tests_name }) => customer_tests_name);
  }

  /**
   * Retrieves the structure of a specific questionnaire.
   *
   * @param {string} questionnaireId the ID of the questionnaire
   *
   * @returns {Questionnaire} the structure of the specified questionnaire
   */
  public getQuestionnaireStructure(questionnaireId: string): Questionnaire {
    return this.get(`questionnaire-structure-${questionnaireId}`);
  }

  /**
   * Stores the structure of a specific questionnaire.
   *
   * @param {string} questionnaireId the ID of the questionnaire
   * @param {Questionnaire} structure the structure to be stored for the specified questionnaire
   */
  public setQuestionnaireStructure(questionnaireId: string, structure: Questionnaire): void {
    this.set(`questionnaire-structure-${questionnaireId}`, structure);
  }

  /**
   * Retrieves the answers for a specific questionnaire.
   *
   * @param {string} questionnaireId the ID of the questionnaire
   *
   * @returns {QuestionnaireAnswers} the answers of the specified questionnaire
   */
  public getQuestionnaireAnswers(questionnaireId: string): QuestionnaireAnswers {
    return this.get(`questionnaire-${questionnaireId}`);
  }

  /**
   * Stores the answers for a specific questionnaire.
   *
   * @param {string}               questionnaireId the ID of the questionnaire
   * @param {QuestionnaireAnswers} answers         the answers to be stored for the specified questionnaire
   */
  public setQuestionnaireAnswers(questionnaireId: string, answers: QuestionnaireAnswers): void {
    this.set(`questionnaire-${questionnaireId}`, answers);
  }

  /**
   * Removes all localStorage items that have the specified suffix.
   *
   * @param {string} suffix the suffix of the items to remove
   */
  public removeItemsWithSuffix(suffix: string): void {
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i)?.endsWith(suffix)) {
        keysToRemove.push(localStorage.key(i));
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  /**
   * Clears from storage all the keys related to the consultation request completed page.
   */
  public removeConsultationRequestCompletedData(): void {
    this.remove(ConsultationStorageKeys.Patient);
    this.remove(ConsultationStorageKeys.TransactionId, false);
    this.remove(ConsultationStorageKeys.Hash);
    this.remove(ConsultationStorageKeys.Questionnaire);
    this.remove(ConsultationStorageKeys.Address);
    this.remove(ConsultationStorageKeys.ShippingAddress);
    this.remove(ConsultationStorageKeys.ShippingAddressId);
    this.remove(ConsultationStorageKeys.Upsells);
    this.remove(ConsultationStorageKeys.ConsultationStatus);
    this.remove(ConsultationStorageKeys.Pharmacy);
    this.remove(ConsultationStorageKeys.UpsellTransactionId, false);
    this.remove(ConsultationStorageKeys.UpsellAddress);
    this.remove(ConsultationStorageKeys.Lab);
    this.remove(`questionnaire-${this.treatmentType}`);
    this.remove(`questionnaire-structure-${this.treatmentType}`);
    this.remove(`questionnaire-${QuestionnaireStorageKeys.Upsells}`);
    this.remove(`questionnaire-${QuestionnaireStorageKeys.AgreementQuestions}`);
    this.remove(ConsultationStorageKeys.TreatmentPreferences);
    this.remove(ConsultationStorageKeys.TreatmentType);
    this.remove(ConsultationStorageKeys.QuestionnaireMedicalHistory);
    this.remove(ConsultationStorageKeys.RemainingBalance);
    this.remove(ConsultationStorageKeys.UpsellUpgrades);
    this.remove(ConsultationStorageKeys.CreateAttachmentToken);
    this.remove(ConsultationStorageKeys.IdAttachmentUploaded, false);
    this.remove(ConsultationStorageKeys.ScheduleOnceId, false);
    this.remove(ConsultationStorageKeys.ConsultationAdditionalInfo, false);
    this.remove(ConsultationStorageKeys.TreatmentFor, false);
    this.removeItemsWithSuffix(this.storageSuffix);
    this.removeConsultationId();
  }

  /** {@inheritDoc AbstractStorageService.remove} */
  public override remove(key: string, usePrefix: boolean = true): void {
    localStorage.removeItem(`${this.getFullKey(key, usePrefix)}${this.storageSuffix}`);
  }

  /** {@inheritDoc AbstractStorageService.get} */
  protected override get(key: string, usePrefix: boolean = true): any {
    return this.jsonParseValue(localStorage.getItem(`${this.getFullKey(key, usePrefix)}${this.storageSuffix}`));
  }

  /** {@inheritDoc AbstractStorageService.set} */
  protected override set(key: string, value: any, usePrefix: boolean = true): void {
    localStorage.setItem(`${this.getFullKey(key, usePrefix)}${this.storageSuffix}`, this.stringifyValue(value));
  }

  /**
   * Removes the consultation ID from the localStorage.
   */
  private removeConsultationId(): void {
    localStorage.removeItem(this.getFullKey(ConsultationStorageKeys.ConsultationId, true));
  }
}
